import React, { useState, useCallback, createContext, useContext } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, DialogContentText, Snackbar, Alert as MuiAlert } from "@mui/material";

const DialogContext = createContext(null);

export const DialogProvider = ({ children }) => {
  const [isInputDialogOpen, setIsInputDialogOpen] = useState(false);
  const [inputDialogValue, setInputDialogValue] = useState("");
  const [inputDialogConfig, setInputDialogConfig] = useState({ title: "", message: "", label: "", initialValue: "" });
  const [resolveInputPromise, setResolveInputPromise] = useState(null);

  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [confirmDialogConfig, setConfirmDialogConfig] = useState({ title: "", message: "" });
  const [resolveConfirmPromise, setResolveConfirmPromise] = useState(null);

  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");

  const promptInput = useCallback((config) => {
    return new Promise((resolve) => {
      setInputDialogConfig(config || { title: "Prompt", message: "", label: "Enter value", initialValue: "" });
      setInputDialogValue(config?.initialValue || "");
      setIsInputDialogOpen(true);
      setResolveInputPromise(() => resolve);
    });
  }, []);

  const handleInputConfirm = () => {
    if (resolveInputPromise) {
      resolveInputPromise(inputDialogValue);
    }
    setIsInputDialogOpen(false);
    setInputDialogValue("");
  };

  const handleInputCancel = () => {
    if (resolveInputPromise) {
      resolveInputPromise(null);
    }
    setIsInputDialogOpen(false);
    setInputDialogValue("");
  };

  const promptConfirm = useCallback((config) => {
    return new Promise((resolve) => {
      setConfirmDialogConfig(config || { title: "Confirm", message: "Are you sure?" });
      setIsConfirmDialogOpen(true);
      setResolveConfirmPromise(() => resolve);
    });
  }, []);

  const handleConfirmAcknowledge = () => {
    if (resolveConfirmPromise) {
      resolveConfirmPromise(true);
    }
    setIsConfirmDialogOpen(false);
  };

  const handleConfirmCancel = () => {
    if (resolveConfirmPromise) {
      resolveConfirmPromise(false);
    }
    setIsConfirmDialogOpen(false);
  };

  const showSnackbar = useCallback((message, severity = "info") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setIsSnackbarOpen(true);
  }, []);

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setIsSnackbarOpen(false);
  };

  return (
    <DialogContext.Provider value={{ promptInput, promptConfirm, showSnackbar }}>
      {children}

      <Dialog open={isInputDialogOpen} onClose={handleInputCancel} maxWidth="sm" fullWidth>
        <DialogTitle>{inputDialogConfig.title}</DialogTitle>
        <DialogContent>
          {inputDialogConfig.message && <DialogContentText>{inputDialogConfig.message}</DialogContentText>}
          <TextField
            autoFocus
            margin="dense"
            label={inputDialogConfig.label}
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={inputDialogValue}
            onChange={(e) => setInputDialogValue(e.target.value)}
            placeholder={inputDialogConfig.label || "Enter value..."}
            sx={{ mt: inputDialogConfig.message ? 2 : 0 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleInputCancel} sx={{ textTransform: "none", borderRadius: 1 }}>
            გაუქმება
          </Button>
          <Button onClick={handleInputConfirm} variant="contained" sx={{ textTransform: "none", borderRadius: 1 }}>
            დადასტურება
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isConfirmDialogOpen} onClose={handleConfirmCancel} maxWidth="xs" fullWidth>
        <DialogTitle>{confirmDialogConfig.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmDialogConfig.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmCancel} sx={{ textTransform: "none", borderRadius: 1 }}>
            გაუქმება
          </Button>
          <Button onClick={handleConfirmAcknowledge} variant="contained" color="primary" autoFocus sx={{ textTransform: "none", borderRadius: 1 }}>
            დადასტურება
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={isSnackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <MuiAlert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: "100%" }} variant="filled">
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </DialogContext.Provider>
  );
};

const useDialog = () => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error("useDialog must be used within a DialogProvider");
  }
  return context;
};

export default useDialog;
